import type { StaffRow, StaffTopPart, StaffWindow, StaffAttribution, StaffLineRow, LostByLineRow } from './types';
import { t } from '../lib/i18n';

const DAEMON_TIMEOUT_MS = 30_000;

async function fetchJSON<T>(url: string, opts: { timeoutMs?: number; daemon?: boolean } = {}): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? 8_000);
  try {
    const res = await fetch(url, { credentials: 'same-origin', signal: ctl.signal });
    if (!res.ok) {
      if (opts.daemon && (res.status === 502 || res.status === 503)) {
        throw new Error(t.errors.daemonOffline);
      }
      if (res.status === 503) throw new Error('cache not ready');
      throw new Error(`HTTP ${res.status}`);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

export type FetchStaffOpts = {
  win: StaffWindow;
  sid?: number;
};

export function fetchSalespeople(opts: FetchStaffOpts): Promise<StaffRow[]> {
  const q = new URLSearchParams({ win: opts.win });
  if (opts.sid !== undefined) q.set('sid', String(opts.sid));
  return fetchJSON<StaffRow[]>(`/api/salespeople?${q}`);
}

export function fetchCounterstaff(opts: FetchStaffOpts): Promise<StaffRow[]> {
  const q = new URLSearchParams({ win: opts.win });
  if (opts.sid !== undefined) q.set('sid', String(opts.sid));
  return fetchJSON<StaffRow[]>(`/api/counterstaff?${q}`);
}

export function fetchStaffTopParts(args: {
  attribution: StaffAttribution;
  id: string;
  win: StaffWindow;
}): Promise<StaffTopPart[]> {
  const q = new URLSearchParams({ attribution: args.attribution, id: args.id, win: args.win });
  return fetchJSON<{ ok: boolean; data: StaffTopPart[]; error?: string }>(
    `/api/staff-top-parts?${q}`,
    { timeoutMs: DAEMON_TIMEOUT_MS, daemon: true },
  ).then((body) => {
    if (!body.ok) throw new Error(body.error || 'daemon error');
    return body.data;
  });
}

// v2.45 — per-staff sales broken out by product line over a date range.
// Live daemon call (the leaderboard cache only has fixed windows).
export function fetchStaffLines(args: {
  attribution: StaffAttribution;
  from: string;
  to: string;
  sid?: number;
}): Promise<StaffLineRow[]> {
  const q = new URLSearchParams({ attribution: args.attribution, from: args.from, to: args.to });
  if (args.sid !== undefined) q.set('sid', String(args.sid));
  return fetchJSON<StaffLineRow[]>(`/api/staff-lines?${q}`, { timeoutMs: DAEMON_TIMEOUT_MS, daemon: true });
}

// v2.56 — lost sales (titm type "L") per (staff, line) over a date range.
// Live daemon call; Go unwraps the daemon envelope to a bare array.
export function fetchStaffLostByLine(args: {
  attribution: StaffAttribution;
  from: string;
  to: string;
  sid?: number;
}): Promise<LostByLineRow[]> {
  const q = new URLSearchParams({ attribution: args.attribution, from: args.from, to: args.to });
  if (args.sid !== undefined) q.set('sid', String(args.sid));
  return fetchJSON<LostByLineRow[]>(`/api/staff-lost-by-line?${q}`, { timeoutMs: DAEMON_TIMEOUT_MS, daemon: true });
}
